import { useEffect, useMemo, useState } from "react";

const EMPTY_OBJ = {};
import { Link } from "react-router-dom";
import { HeartHandshake, Loader2, History, Plus, Minus, ChevronDown, ChevronUp, Star } from "lucide-react";
import api from "../../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../../lib/bookingStatus";
import { deDateMediumTime, deDateShortTime } from "../../lib/dateFormat";

/**
 * Feb 2026 · Read-only Anzeige der Kunden-Präferenzen im Admin-/Concierge-Detail.
 *
 * Feb 2026 · 9 Sep 2026 · User-Directive „Hauptobjekt-Präferenzen":
 * Zeigt jetzt gezielt die Präferenzen des vom Kunden markierten
 * Hauptobjekts (via Backend `_resolve_primary_property`). Fällt der Kunde
 * keins gesetzt hat, wird auf das erste archivfreie Objekt zurückgegriffen
 * und ein goldener Chip „Kein Hauptobjekt gewählt" macht den Fallback
 * transparent. Damit sehen Admins nie mehr gemischte Präferenzen über
 * mehrere Objekte hinweg — sondern immer die des Hauptobjekts.
 *
 * Zeigt aktuelle Präferenzen + optional die letzte vorherige Version mit
 * hinzugefügten (grün) / entfernten (rot) Tags — so sieht der Concierge auf
 * einen Blick, was seit dem letzten Update anders ist. Der volle History-Stack
 * (max. 10) ist per Toggle einblendbar.
 */
export function CustomerPreferencesCard({ customerId }) {
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    (async () => {
      try {
        const [{ data: pref }, { data: hist }] = await Promise.all([
          api.get(`/team/customers/${customerId}/preferences`),
          api.get(`/team/customers/${customerId}/preferences/history`).catch(() => ({ data: { history: [] } })),
        ]);
        setPrefs(pref);
        setHistory(hist?.history || []);
      } catch (e) {
        console.debug("team preferences load failed:", e?.response?.status);
      } finally { setLoading(false); }
    })();
  }, [customerId]);

  const items = prefs || EMPTY_OBJ;
  const propertyName = items.property_name || null;
  const isPrimarySet = !!items.is_primary_set;
  const hasAny = (
    (items.allergies || []).length > 0 ||
    (items.food || []).length > 0 ||
    (items.home || []).length > 0 ||
    (items.notes || "").trim().length > 0
  );
  const updated = items.updated_at
    ? deDateMediumTime(items.updated_at)
    : null;

  const previous = history[0]?.snapshot || null;
  const diff = useMemo(() => computeDiff(previous, items), [previous, items]);
  const hasDiff = previous && (
    diff.allergies.added.length + diff.allergies.removed.length +
    diff.food.added.length + diff.food.removed.length +
    diff.home.added.length + diff.home.removed.length +
    (diff.notes.changed ? 1 : 0)
  ) > 0;
  const previousReplacedAt = history[0]?.replaced_at
    ? deDateShortTime(history[0].replaced_at)
    : null;

  return (
    <div
      className="bg-white border border-navy/10 p-5 md:p-6 mb-8"
      data-testid="team-customer-preferences-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="label-caps mb-1">Weissblom</div>
          <h2 className="font-display text-xl md:text-2xl text-navy leading-tight inline-flex items-center gap-2">
            <HeartHandshake className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
            Präferenzen
          </h2>
        </div>
        {updated && (
          <div className="text-[11px] tracking-widest uppercase text-navy-dark/50" data-testid="team-prefs-updated">
            zuletzt aktualisiert · {updated}
          </div>
        )}
      </div>

      {propertyName && (
        <div
          className="flex items-center gap-2 flex-wrap mb-4 pb-3 border-b border-navy/5"
          data-testid="team-prefs-property-scope"
        >
          <span className="label-caps text-navy-dark/55">
            {isPrimarySet ? "Hauptobjekt" : "Aktives Objekt"}
          </span>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11.5px] tracking-wide border"
            style={
              isPrimarySet
                ? { background: withAlpha(BRAND_COLORS.gold, 0.10), color: BRAND_COLORS.gold, borderColor: withAlpha(BRAND_COLORS.gold, 0.55) }
                : { background: "rgba(13,25,48,0.03)", color: "rgba(13,25,48,0.75)", borderColor: "rgba(13,25,48,0.18)" }
            }
            data-testid="team-prefs-property-name"
          >
            <Star
              className="w-3 h-3"
              strokeWidth={1.8}
              style={{ color: BRAND_COLORS.gold }}
              {...(isPrimarySet ? { fill: BRAND_COLORS.gold } : {})}
            />
            {propertyName}
          </span>
          {!isPrimarySet && (
            <span
              className="text-[11.5px] italic"
              style={{ color: BRAND_COLORS.gold }}
              data-testid="team-prefs-fallback-hint"
            >
              Kein Hauptobjekt gewählt — zeigt Standardobjekt
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-navy-dark/60 text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin" /> Wird geladen …
        </div>
      ) : !hasAny ? (
        <div className="text-[13px] text-navy-dark/55 italic" data-testid="team-prefs-empty">
          Noch keine Präferenzen hinterlegt — Kunde wird beim ersten Speichern automatisch informiert.
        </div>
      ) : (
        <div className="space-y-4">
          <PrefRow label="Allergien" tags={items.allergies || []} diff={diff.allergies} testid="team-prefs-allergies" />
          <PrefRow label="Ernährung" tags={items.food || []} diff={diff.food} testid="team-prefs-food" />
          <PrefRow label="Zuhause" tags={items.home || []} diff={diff.home} testid="team-prefs-home" />
          {(items.notes || "").trim() && (
            <div data-testid="team-prefs-notes">
              <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Notiz</div>
              <div className="text-[13px] text-navy-dark leading-relaxed italic whitespace-pre-wrap">
                {items.notes}
              </div>
              {diff.notes.changed && (previous.notes || "").trim() && (
                <div
                  className="mt-2 text-[12px] text-navy-dark/60 leading-relaxed italic pl-3 border-l-2"
                  style={{ borderColor: withAlpha(STATUS_COLORS.red, 0.35) }}
                  data-testid="team-prefs-notes-previous"
                >
                  <span className="label-caps text-navy-dark/50 mr-2">vorher</span>
                  <span className="line-through decoration-navy-dark/30">{previous.notes}</span>
                </div>
              )}
            </div>
          )}

          {previous && (
            <div className="pt-3 border-t border-navy/10" data-testid="team-prefs-history-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[11px] tracking-widest uppercase text-navy-dark/55 inline-flex items-center gap-1.5">
                  <History className="w-3.5 h-3.5" strokeWidth={1.6} />
                  {hasDiff
                    ? <>Änderung seit <span className="text-navy-dark">{previousReplacedAt}</span></>
                    : <>Keine strukturellen Änderungen · vorherige Version {previousReplacedAt}</>}
                </div>
                <button
                  type="button"
                  onClick={() => setHistoryOpen((v) => !v)}
                  data-testid="team-prefs-history-toggle"
                  className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy inline-flex items-center gap-1"
                >
                  {historyOpen ? "Verlauf schließen" : `Verlauf ansehen (${history.length})`}
                  {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>

              {historyOpen && (
                <ol className="mt-3 space-y-4" data-testid="team-prefs-history-list">
                  {history.map((h, i) => (
                    <li key={h.replaced_at + i} className="pl-3 border-l-2 border-navy/10">
                      <div className="text-[11px] tracking-widest uppercase text-navy-dark/50 mb-1.5">
                        Version -{i + 1} · zurückgesetzt am{" "}
                        {deDateMediumTime(h.replaced_at)}
                      </div>
                      <HistoryVersion snapshot={h.snapshot} testid={`team-prefs-history-${i}`} />
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function computeDiff(previous, current) {
  const empty = { added: [], removed: [] };
  if (!previous) {
    return { allergies: empty, food: empty, home: empty, notes: { changed: false } };
  }
  const cmp = (was, is) => {
    const wasSet = new Set(was || []);
    const isSet = new Set(is || []);
    return {
      added: [...isSet].filter((x) => !wasSet.has(x)),
      removed: [...wasSet].filter((x) => !isSet.has(x)),
    };
  };
  return {
    allergies: cmp(previous.allergies, current.allergies),
    food: cmp(previous.food, current.food),
    home: cmp(previous.home, current.home),
    notes: { changed: (previous.notes || "") !== (current.notes || "") },
  };
}

function PrefRow({ label, tags, diff, testid }) {
  const nothing = (!tags || tags.length === 0) && (!diff || (diff.removed.length === 0));
  if (nothing) return null;
  const addedSet = new Set(diff?.added || []);
  return (
    <div data-testid={testid}>
      <div className="label-caps mb-1.5" style={{ color: BRAND_COLORS.gold }}>{label}</div>
      <div className="flex flex-wrap gap-2">
        {tags.map((t, i) => {
          const isAdded = addedSet.has(t);
          return (
            <span
              key={`${t}-${i}`}
              className={`text-[12px] tracking-wide px-3 py-1 border ${isAdded ? "" : "bg-ivory/70 text-navy-dark border-navy/15"}`}
              style={isAdded ? {
                background: withAlpha(STATUS_COLORS.green, 0.10),
                color: BRAND_COLORS.greenDark || "#1F5233",
                borderColor: withAlpha(STATUS_COLORS.green, 0.45),
              } : {}}
              data-testid={`${testid}-tag-${i}`}
              title={isAdded ? "Neu seit letzter Änderung" : undefined}
            >
              {isAdded && <Plus className="w-3 h-3 inline mr-1" strokeWidth={2} />}
              {t}
            </span>
          );
        })}
        {(diff?.removed || []).map((t, i) => (
          <span
            key={`removed-${t}-${i}`}
            className="text-[12px] tracking-wide px-3 py-1 border line-through"
            style={{
              background: withAlpha(STATUS_COLORS.red, 0.06),
              color: "#5C1616",
              borderColor: withAlpha(STATUS_COLORS.red, 0.35),
              textDecorationColor: withAlpha(STATUS_COLORS.red, 0.6),
            }}
            data-testid={`${testid}-removed-${i}`}
            title="Vor der letzten Änderung noch aktiv"
          >
            <Minus className="w-3 h-3 inline mr-1" strokeWidth={2} />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function HistoryVersion({ snapshot, testid }) {
  if (!snapshot) return null;
  const parts = [];
  const push = (label, arr) => {
    if (arr && arr.length) parts.push({ label, arr });
  };
  push("Allergien", snapshot.allergies);
  push("Ernährung", snapshot.food);
  push("Zuhause", snapshot.home);
  const hasNote = (snapshot.notes || "").trim().length > 0;
  if (parts.length === 0 && !hasNote) {
    return <div className="text-[12px] text-navy-dark/50 italic">— leere Version —</div>;
  }
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {parts.map((p) => (
        <div key={p.label} className="text-[12px] text-navy-dark/80">
          <span className="label-caps text-navy-dark/50 mr-2">{p.label}</span>
          <span>{p.arr.join(", ")}</span>
        </div>
      ))}
      {hasNote && (
        <div className="text-[12px] text-navy-dark/70 italic">
          <span className="label-caps not-italic text-navy-dark/50 mr-2">Notiz</span>
          {snapshot.notes}
        </div>
      )}
    </div>
  );
}
